/**
 * The floating Combo control surface — ported from `combo_slider_card.dart`.
 *
 *   * Layer/opacity card (bottom) — three sub-layer thumbnails that switch the
 *     active layer, a vertical opacity slider, and the × close.
 *   * Playback pill (top) — a dark caption bar (frame date-time · layer) over a
 *     light-glass pill with play/pause + ⚙ tune + a stretched scrub slider.
 *   * Gradient bar (under the pill) — a horizontal colour bar; for SST/CHL an
 *     interactive dual-thumb range control, for wind the read-only kn scale.
 *
 * The ⚙ options panel is shown without a dim/blur scrim so the live map recolour
 * shows while the range is dragged.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ComboService, ComboState, defaultComboState } from './combo.service';
import {
  cmax,
  cmin,
  currentWindFrameUrl,
  liveIndex,
  playStart,
  resolvedFrameIndex,
  stateCfg,
} from './combo.service';
import { ComboConstants } from './combo.constants';
import {
  ComboFrame,
  ComboLayer,
  fmtDispForUser,
  fmtDispPartsForUser,
  layerCfgOf,
  unitLabelForUser,
} from './combo.models';
import { UserSettingsService } from '../../../../services/user-settings.service';
import { speedFromMs } from '../../../../shared/utils/unit-formatter.util';
import { comboEasternZone, comboToEastern } from './combo-time';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';

@Component({
  selector: 'app-combo-slider-card',
  standalone: true,
  imports: [CommonModule, FormsModule, TourAnchorDirective],
  templateUrl: './combo-slider-card.component.html',
  styleUrl: './combo-slider-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboSliderCardComponent implements OnInit, OnDestroy {
  /** Forces an "enjoy the map" collapse from outside (e.g. a waypoint popup). */
  @Input() externallyCollapsed = false;

  /** Disables the Combo layer entirely (the × button). */
  @Output() closeCombo = new EventEmitter<void>();
  /** ⚡ Auto-view — host passes the live map viewport to /api/stretch. */
  @Output() autoView = new EventEmitter<void>();
  /** Fired when the user collapses into "enjoy the map" via the collapse button. */
  @Output() enterFullscreen = new EventEmitter<void>();
  /** Open the 5-model wind chart for the current probe (host handles it). */
  @Output() openModels = new EventEmitter<void>();

  readonly ink = '#001A33';

  state: ComboState = defaultComboState();
  imageInFlight = 0;
  windArrowsBusy = 0;
  windFieldPainted = false;

  /** User's own "enjoy the map" collapse toggle. */
  collapsed = false;

  /** Live frame-slider thumb position while dragging (null when not dragging). */
  dragFrame: number | null = null;
  private frameCommit: any = null;

  /** ⚙ options panel visibility (inline, no scrim). */
  showOptions = false;

  private subs: Subscription[] = [];

  constructor(
    public combo: ComboService,
    private cdr: ChangeDetectorRef,
    private userSettings: UserSettingsService,
  ) {}

  ngOnInit(): void {
    // Legend endpoints, the range readout and the wind scale all render in the
    // viewer's units now, so a Settings change has to re-run change detection
    // here the same way it does on the Watch/Trips/Waypoints cards.
    this.userSettings.ensureLoaded();
    this.subs.push(
      this.userSettings.settings$.subscribe(() => this.cdr.markForCheck()),
      this.combo.state$.subscribe((s) => {
        this.state = s;
        this.cdr.markForCheck();
      }),
      this.combo.imageInFlight$.subscribe((n) => {
        this.imageInFlight = n;
        this.cdr.markForCheck();
      }),
      this.combo.windArrowsBusy$.subscribe((n) => {
        this.windArrowsBusy = n;
        this.cdr.markForCheck();
      }),
      this.combo.windFieldPainted$.subscribe((p) => {
        this.windFieldPainted = p;
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.frameCommit) clearTimeout(this.frameCommit);
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ── Derived view state ────────────────────────────────────────────────────

  get isCollapsed(): boolean {
    return this.externallyCollapsed || this.collapsed;
  }
  get cfg() {
    return stateCfg(this.state);
  }
  get isWind(): boolean {
    return this.state.config.layer === 'wind';
  }
  get accent(): string {
    return this.cfg.accent;
  }
  get opacity(): number {
    return Math.max(0.1, Math.min(1.0, this.state.config.opacity));
  }
  get opacityPct(): number {
    return Math.round(this.opacity * 100);
  }
  get enabled(): boolean {
    return this.state.config.enabled;
  }

  /** Gradient CSS for a layer's colormap — full fixed rainbow, matches mobile. */
  gradientCss(layer: ComboLayer): string {
    const cfg = layerCfgOf(layer);
    return `linear-gradient(to right, ${cfg.gradient.join(', ')})`;
  }

  shortName(l: ComboLayer): string {
    switch (l) {
      case 'sst':
        return 'GOES-SST';
      case 'chl':
        return 'Multi-CHL';
      case 'wind':
        return 'METEO-WND';
      case 'sss':
        return 'CMEMS-SAL';
      case 'cur':
        return 'CMEMS-CUR';
      case 'fsle':
        return 'CMEMS-FSLE';
    }
  }

  // ── Frame playback ─────────────────────────────────────────────────────────

  private get manifestFrames(): ComboFrame[] {
    return this.state.manifest?.frames ?? [];
  }
  get frameCount(): number {
    return this.isWind ? this.state.windFrames.length : this.manifestFrames.length;
  }
  get frameStart(): number {
    return this.isWind ? 0 : playStart(this.state);
  }
  get frameLast(): number {
    if (this.isWind) return this.frameCount === 0 ? 0 : this.frameCount - 1;
    // Scrub only up to "now"; forecast-tail frames (SSS/CUR) stay hidden.
    return this.manifestFrames.length === 0 ? 0 : liveIndex(this.state);
  }
  get hasFrames(): boolean {
    return this.frameCount > 1 && this.frameStart < this.frameLast;
  }
  get frameIdx(): number {
    if (this.isWind) {
      return Math.max(0, Math.min(this.frameLast, this.state.windFrameIndex));
    }
    // Composite shown: park the thumb on the newest frame that has data (≤ now),
    // so it agrees with the gap-filled "latest" composite instead of a
    // possibly-empty / forecast-tail trailing slot.
    if (this.state.config.compositeChecked && this.state.manifest?.composite != null) {
      return this.newestCoveredIndex();
    }
    return resolvedFrameIndex(this.state);
  }

  /**
   * Newest frame that actually carries data (coverage > 0) at or before "now" —
   * the composite's effective time. Never a forecast-tail frame: the scan starts
   * at {@link liveIndex}, so a future-stamped slot can't be selected even if the
   * backend primed it with coverage.
   */
  private newestCoveredIndex(): number {
    const frames = this.state.manifest?.frames ?? [];
    if (frames.length === 0) return 0;
    const live = liveIndex(this.state);
    for (let i = live; i >= 0; i--) {
      if ((frames[i].coverage ?? 1) > 0) return i;
    }
    return live;
  }
  get sliderValue(): number {
    if (!this.hasFrames) return 0;
    const v = this.dragFrame ?? this.frameIdx;
    return Math.max(this.frameStart, Math.min(this.frameLast, v));
  }

  get isPlaying(): boolean {
    return this.state.isPlaying;
  }

  togglePlay(): void {
    if (this.hasFrames) this.combo.togglePlay();
  }

  onFrameScrub(value: number): void {
    const v = Math.round(value);
    this.dragFrame = v;
    if (this.isWind) {
      this.combo.setWindFrame(v);
      return;
    }
    if (this.frameCommit) clearTimeout(this.frameCommit);
    this.frameCommit = setTimeout(
      () => this.combo.setFrameIndex(v),
      ComboConstants.frameCommitMs,
    );
  }

  onFrameScrubEnd(value: number): void {
    const v = Math.round(value);
    if (this.frameCommit) clearTimeout(this.frameCommit);
    if (this.isWind) this.combo.setWindFrame(v);
    else this.combo.setFrameIndex(v);
    this.dragFrame = null;
  }

  // ── Color range (SST/CHL dual-thumb) ───────────────────────────────────────

  get rangeInteractive(): boolean {
    return !this.isWind && this.combo.caps.render && this.state.manifest != null;
  }
  get cmin(): number {
    return Math.max(this.cfg.sliderMin, Math.min(this.cfg.sliderMax, cmin(this.state)));
  }
  get cmax(): number {
    return Math.max(this.cfg.sliderMin, Math.min(this.cfg.sliderMax, cmax(this.state)));
  }
  // Legend endpoints. The slider domain itself stays in the layer's native unit
  // (it drives what the map paints — see `fmtDispPartsForUser`); only the text
  // shown to the viewer is converted to their preference.
  get cminLabel(): string {
    return fmtDispForUser(this.cfg, this.cmin, this.userSettings.snapshot);
  }
  get cmaxLabel(): string {
    return fmtDispForUser(this.cfg, this.cmax, this.userSettings.snapshot);
  }
  /** Thumb left % from a native value. */
  rangePct(v: number): number {
    const cfg = this.cfg;
    return ((v - cfg.sliderMin) / (cfg.sliderMax - cfg.sliderMin)) * 100;
  }

  // ── Custom pointer-driven range control ─────────────────────────────────
  /** Which thumb is currently being dragged / was tapped closest — used for
   *  the `.active` visual state and to route pointermove updates. */
  rangeActiveThumb: 'min' | 'max' | null = null;

  onRangePointerDown(ev: PointerEvent): void {
    if (!this.rangeInteractive) return;
    const track = ev.currentTarget as HTMLElement;
    const val = this.pointerToValue(ev, track);
    // Whichever thumb is closer to the tapped value jumps to it — the mobile
    // app behaviour Ryan wants (native <input type=range> doesn't do this).
    const dMin = Math.abs(val - this.cmin);
    const dMax = Math.abs(val - this.cmax);
    this.rangeActiveThumb = dMin <= dMax ? 'min' : 'max';
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    // capture on the track element too so drags keep flowing to it
    try {
      track.setPointerCapture?.(ev.pointerId);
    } catch {
      /* ignore */
    }
    this.updateActiveThumb(val);
  }

  onRangePointerMove(ev: PointerEvent): void {
    if (!this.rangeInteractive || this.rangeActiveThumb == null) return;
    const track = ev.currentTarget as HTMLElement;
    const val = this.pointerToValue(ev, track);
    this.updateActiveThumb(val);
  }

  onRangePointerUp(ev: PointerEvent): void {
    if (this.rangeActiveThumb == null) return;
    this.rangeActiveThumb = null;
    try {
      (ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId);
    } catch {
      /* ignore */
    }
  }

  /** Convert a pointer event's x-position on the track to a value in the
   *  layer's native [sliderMin, sliderMax] domain, snapped to `sliderStep`. */
  private pointerToValue(ev: PointerEvent, track: HTMLElement): number {
    const rect = track.getBoundingClientRect();
    const pct = rect.width <= 0 ? 0 : (ev.clientX - rect.left) / rect.width;
    const cfg = this.cfg;
    const domain = cfg.sliderMax - cfg.sliderMin;
    let raw = cfg.sliderMin + Math.max(0, Math.min(1, pct)) * domain;
    // snap to step
    const step = cfg.sliderStep;
    raw = Math.round(raw / step) * step;
    return raw;
  }

  private updateActiveThumb(newValue: number): void {
    if (this.rangeActiveThumb === 'min') {
      this.applyRange(newValue, this.cmax);
    } else if (this.rangeActiveThumb === 'max') {
      this.applyRange(this.cmin, newValue);
    }
  }

  private applyRange(start: number, end: number): void {
    if (!this.rangeInteractive) return;
    const cfg = this.cfg;
    const step = cfg.sliderStep;
    let lo = Math.min(start, end - step);
    let hi = Math.max(end, start + step);
    lo = Math.max(cfg.sliderMin, Math.min(cfg.sliderMax, lo));
    hi = Math.max(cfg.sliderMin, Math.min(cfg.sliderMax, hi));
    this.combo.setRange(lo, hi);
  }

  /**
   * Read-only wind scale ticks. The ramp is baked in knots (0–40), so the tick
   * POSITIONS are fixed; only their captions follow the viewer's speed unit.
   * These were the literal strings `0 … 40 kn`, so the scale still read knots
   * after switching Speed to mph.
   */
  get windTicks(): string[] {
    const settings = this.userSettings.snapshot;
    const knots = [0, 10, 20, 30, 40];
    return knots.map((kn, i) => {
      const parts = speedFromMs(kn * 0.514444, settings.speedUnit, 0);
      return i === knots.length - 1 ? `${parts.value} ${parts.unit}` : parts.value;
    });
  }

  // ── Caption ─────────────────────────────────────────────────────────────────

  private static readonly WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  private static readonly MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  get captionTitle(): string {
    if (this.isWind && this.windArrowsBusy > 0) return 'Scaling wind arrows…';
    if (this.imageInFlight > 0 && !this.isPlaying) return 'Loading frames…';
    return this.frameLabel();
  }

  private frameLabel(): string {
    const cfg = this.cfg;
    if (this.isWind) {
      const wf = this.state.windField;
      // Hold "building 7-day forecast…" until MapLibre has actually painted the
      // coloured field on the map (windFieldPainted). Previously the time-label
      // appeared the moment the first baked frame was in the array, ~5 s before
      // the overlay was actually visible — which read as "time is opened, but
      // overlay hasn't loaded".
      if (
        this.windFieldPainted &&
        wf != null &&
        wf.times.length > 0 &&
        this.state.windFrames.length > 0
      ) {
        const i = Math.max(0, Math.min(wf.times.length - 1, this.state.windFrameIndex));
        return this.fcLabel(wf.times[i]);
      }
      return 'building 7-day forecast…';
    }
    const m = this.state.manifest;
    if (m == null || m.frames.length === 0) {
      return this.state.manifestLoading ? 'Loading frames…' : (this.state.config.date ?? '—');
    }
    const f =
      this.state.config.compositeChecked && m.composite != null
        ? m.frames[this.newestCoveredIndex()]
        : m.frames[resolvedFrameIndex(this.state)];
    if (cfg.cadence === 'day') return this.fmtDayKey(f.time);
    return `${this.fmtFrameTime(comboToEastern(f.iso), cfg.cadence)} ${comboEasternZone(f.iso)}`;
  }

  private fcLabel(t: string): string {
    const hasZone = t.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(t);
    const utc = new Date(hasZone ? t : `${t}Z`);
    const d = comboToEastern(utc);
    const two = (n: number) => String(n).padStart(2, '0');
    return `${ComboSliderCardComponent.WD[d.getUTCDay()]} ${two(d.getUTCMonth() + 1)}/${two(d.getUTCDate())} ${two(d.getUTCHours())}:${two(d.getUTCMinutes())} ${comboEasternZone(utc)}`;
  }

  private fmtDayKey(key: string): string {
    if (key.length >= 8) {
      const mo = parseInt(key.substring(4, 6), 10);
      const d = parseInt(key.substring(6, 8), 10);
      if (!Number.isNaN(mo) && mo >= 1 && mo <= 12 && !Number.isNaN(d)) {
        return `${ComboSliderCardComponent.MONTHS[mo - 1]} ${d}`;
      }
    }
    return key;
  }

  private fmtFrameTime(d: Date, cadence: string): string {
    const base = `${ComboSliderCardComponent.MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
    if (cadence === 'day') return base;
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${base} ${hh}:${mm}`;
  }

  // ── Collapse / options ────────────────────────────────────────────────────

  toggleCollapse(): void {
    const entering = !this.collapsed;
    this.collapsed = !this.collapsed;
    if (entering) this.enterFullscreen.emit();
  }

  openOptions(): void {
    if (this.isWind) return;
    this.showOptions = true;
  }
  closeOptions(): void {
    this.showOptions = false;
  }

  onClose(): void {
    this.closeCombo.emit();
  }

  // ── Options panel readouts/actions ─────────────────────────────────────────

  get rangeReadout(): string {
    const settings = this.userSettings.snapshot;
    const lo = fmtDispPartsForUser(this.cfg, this.cmin, settings).value;
    const hi = fmtDispPartsForUser(this.cfg, this.cmax, settings).value;
    return `${lo}–${hi} ${unitLabelForUser(this.cfg, settings)}`;
  }
  get rangeEnabled(): boolean {
    return this.combo.caps.render && this.state.manifest != null;
  }
  get stretchEnabled(): boolean {
    return this.combo.caps.stretch && this.state.manifest != null;
  }
  get hasCustomRange(): boolean {
    return this.state.config.customVmin != null && this.state.config.customVmax != null;
  }
  get hasComposite(): boolean {
    return this.state.manifest?.composite != null;
  }
  get coverageLine(): string | null {
    const m = this.state.manifest;
    if (m == null || m.frames.length === 0) return null;
    const isComposite = this.state.config.compositeChecked && m.composite != null;
    const cov = isComposite ? m.composite!.coverage : m.frames[resolvedFrameIndex(this.state)].coverage;
    if (cov == null) return null;
    const pct = Math.round(cov <= 1 ? cov * 100 : cov);
    return isComposite ? `ocean ${pct}% (gap-filled)` : `coverage ${pct}%`;
  }

  onAutoView(): void {
    this.autoView.emit();
    this.closeOptions();
  }
  onReset(): void {
    if (this.hasCustomRange) this.combo.resetRange();
  }
  onToggleComposite(on: boolean): void {
    this.combo.setComposite(on);
  }
  onToggleFronts(): void {
    this.combo.toggleFronts();
  }
}
